import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { FleetPage, FleetSection, Notice } from '../../../../shared/sso-workspace/fleet-page'
import { FleetGrid, useGridList } from '../../../../shared/sso-workspace/fleet-grid'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { Drawer } from '../../../../shared/drawer/drawer'
import { TableAgClient } from '../../../../shared/table-builder/table-ag-client'
import { ROW_HEIGHT_TWO_LINE } from '../../../../shared/table-builder/table-ag'
import { ColumnFormat, ColumnMenu } from '../../../../shared/table-builder/table-columns'
import { Menu, MenuDelete, MenuItem, MenuRestore } from '../../../../shared/menu/menu'
import { MenuHeaderContext } from '../../../../shared/menu/menu-context'
import { IconAiDocument, IconCheck } from '../../../../shared/icon/icon'
import { serverMessage } from '../../../../shared/error/server-message'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { useRoleSysAiToolsTelemetry } from '../../../../role/hook/role-sys'
import { aiToolsAvailable, getPostmortem } from './ai-tools-api'
import { MarkPills, POSTMORTEM_SOFT_LIMIT_NOTE, findingRow, isUnlinked, jsonText, marksCell, postmortemRow, severityTone } from './ai-tools-words'
import { BinnedNotice, PersonValue, Row, UnlinkedNote, When, useRecordWrites } from './detail-parts'
import { MarkDialog } from './mark-dialog'
import { useConsumers } from './use-consumers'
import { AiToolsTrail, tabCrumb } from './ai-tools-trail'
import '../../org/api-keys/api-keys.scss'

// One postmortem (contract §4.6): what it says, its session (a link to the
// session's page), who it is linked to (or Unlinked), when it was written
// and received, its size, each consumer's mark, its findings (in the
// postmortem's own order, each openable as received) and the record as
// received. A postmortem's read answers its findings with it, so they are a
// client-side grid of a few rows, not a paged list; the record and each
// finding's JSON show only in a scrolling block or the drawer, never in a
// grid cell. The page header's menu (the shared Menu) is Mark postmortem
// (SYS_AI_TOOLS_TELEMETRY update) and Delete postmortem, or Restore
// postmortem for one in the recycle bin (delete); a binned postmortem says
// so above everything, with Restore. A finding's row has Delete (its trash
// icon) and Mark (its ⋮). The postmortem's History is its record route's
// (record-routes.js: staff's moves, restores and marks of it are
// ai_tools.record_* events on it).
//
// The findings keep the postmortem's order: a client grid sorts its first
// column when it is ready, so that column is the findings' own number (the
// server's ordinal), and the sort chip then says "Sorted by Order, lowest
// first", which is the truth, rather than an alphabetical order of titles.
//
// A mark re-reads the postmortem (its marks and its findings' marks) and the
// consumers (a new name may have joined). Its dialog is page state: a save
// that finishes after its dialog was cancelled still counts, but closes only
// its own dialog, never one opened since. The open finding is page state too
// (its id: it is read from the latest read, so its marks stay current).

const FAILED = "This postmortem couldn't be loaded."

// AG Grid 33 reads getRowId once: a stable, module-level function.
const findingID = params => String(params.data.id)

// Grid cells (module-level, so AG Grid keeps its cells).
const orderCell = cell => (cell.data && typeof cell.data.ordinal === 'number' ? String(cell.data.ordinal + 1) : '')
const titleCell = cell => (cell.data
  ? <span className="ak-name-cell"><span className="fw-semibold text-truncate">{cell.data.titleText}</span><span className="sw-cell-sub text-truncate">{cell.data.whatText}</span></span>
  : '')
const severityCell = cell => (cell.data && cell.data.severityText ? <Pill tone={severityTone(cell.data.severityText)}>{cell.data.severityText}</Pill> : '')
const categoryCell = cell => (cell.data ? cell.data.categoryText : '')
const toolCell = cell => (cell.data ? <span className="sw-mono">{cell.data.toolText}</span> : '')

const FindingDrawer = ({ row, onClose }) => (
  <Drawer open size="lg" title={`Finding: ${row.titleText}`} onClose={onClose}>
    <dl className="sw-dl">
      <Row label="Severity">{row.severityText ? <Pill tone={severityTone(row.severityText)}>{row.severityText}</Pill> : ''}</Row>
      <Row label="Category">{row.categoryText}</Row>
      <Row label="Tool">{row.toolText ? <span className="sw-mono">{row.toolText}</span> : ''}</Row>
      <Row label="What happened">{row.whatText}</Row>
      <Row label="Workaround">{row.workaround}</Row>
      <Row label="Proposal">{row.proposal}</Row>
      <Row label="Marks"><MarkPills marks={row.marks} /></Row>
    </dl>
    <section aria-label="Finding as received">
      <h3 className="h6">Finding as received</h3>
      <pre className="ak-body sw-mono">{jsonText(row.body)}</pre>
    </section>
  </Drawer>
)

const readDetail = body => {
  const postmortem = postmortemRow(body.postmortem)
  const findings = (Array.isArray(body.findings) ? body.findings : []).map(f => findingRow({ ...f, postmortemId: postmortem.id, ts: postmortem.ts }))
  return { postmortem, findings }
}

const PostmortemPage = ({ postmortemID }) => {
  const available = aiToolsAvailable()
  const role = useRoleSysAiToolsTelemetry()
  const [consumers, reloadConsumers] = useConsumers()
  const [detail, setDetail] = useState(undefined)
  const [error, failure, setError] = useErrorLine()
  const [openID, setOpenID] = useState(null)
  const [marking, setMarking] = useState(null)
  const tableRef = useRef()
  const reads = useRef(0)

  // undefined while the first read is out, null when it failed. A read after a
  // mark that fails keeps the postmortem that is shown, with the notice above
  // it; an answer to an earlier read never replaces a later one's.
  const load = useCallback(() => {
    if (!aiToolsAvailable()) return Promise.resolve()
    const mine = ++reads.current
    return getPostmortem(postmortemID).then(
      body => {
        if (mine !== reads.current) return
        if (body && body.postmortem) { setError(null); setDetail(readDetail(body)) } else { setDetail(current => current || null); setError(FAILED) }
      },
      err => {
        if (mine !== reads.current) return
        setDetail(current => current || null)
        setError(serverMessage(err, FAILED), err)
      }
    )
  }, [postmortemID, setError])
  useEffect(() => { load() }, [load])

  const gridList = useGridList(detail ? detail.findings : detail)
  const binned = !!(detail && detail.postmortem.deletedAt)
  const canMark = !!role.accessUpdate && !binned
  const canDelete = !!role.accessDelete
  const writes = useRecordWrites({ kind: 'postmortem', onChanged: load })
  const findingWrites = useRecordWrites({ kind: 'finding', onChanged: load })
  const askDeleteFinding = findingWrites.askDelete

  const askMark = useCallback(target => {
    if (!(consumers && consumers.length)) reloadConsumers()
    setMarking(target)
  }, [consumers, reloadConsumers])
  const menu = useMemo(() => row => (
    <Menu small>
      <MenuDelete show={canDelete && !binned} onClick={() => askDeleteFinding(row.id)} />
      <MenuItem Icon={IconCheck} text="Mark" show={canMark} onClick={() => askMark({ kind: 'finding', id: row.id, label: `finding "${row.titleText}"` })} />
    </Menu>
  ), [canMark, canDelete, binned, askMark, askDeleteFinding])
  const marked = target => {
    setMarking(current => (current === target ? null : current))
    load()
    reloadConsumers()
  }

  const p = detail && detail.postmortem
  const openRow = openID !== null && detail ? detail.findings.find(f => f.id === openID) : null
  const headerMenu = available && p && (canMark || canDelete)
    ? <MenuHeaderContext.Provider value={true}>
        <Menu>
          <MenuItem Icon={IconCheck} text="Mark postmortem" pin show={canMark} onClick={() => askMark({ kind: 'postmortem', id: p.id, label: `postmortem "${p.titleText}"` })} />
          <MenuDelete text="Delete postmortem" show={canDelete && !binned} onClick={() => writes.askDelete(p.id)} />
          <MenuRestore text="Restore postmortem" show={canDelete && binned} disabled={writes.busy} onClick={() => writes.restore(p.id)} />
        </Menu>
      </MenuHeaderContext.Provider>
    : null
  return (
    <FleetPage
      title={p ? p.titleText : 'Postmortem'}
      purpose={p ? (p.channel ? `${p.sourceText} · ${p.channel}` : p.sourceText) : null}
      actions={headerMenu}
      testId="ai-tools-postmortem-page">
      <AiToolsTrail crumbs={[tabCrumb('postmortems'), { url: `/admin/ai-tools/postmortems/${postmortemID}`, text: p ? p.titleText : '' }]} />
      <p className="mb-2"><Link to="/admin/ai-tools?tab=postmortems">← All postmortems</Link></p>
      {!available && <Notice tone="warn">AI tools telemetry isn't available in this build.</Notice>}
      {error && <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice>}
      {available && p && binned && <BinnedNotice row={p} kind="postmortem" />}
      {writes.notice}
      {findingWrites.notice}
      {available && detail === undefined && !error && <p className="sw-hint" role="status">Loading…</p>}
      {available && p &&
        <>
          <FleetSection id="ait-postmortem" title="About this postmortem">
            <dl className="sw-dl">
              <Row label="Summary">{p.summary}</Row>
              <Row label="Session">{p.sessionId ? <Link className="sw-mono" to={`/admin/ai-tools/sessions/${p.sessionId}`}>{p.sessionText}</Link> : p.sessionText}</Row>
              <Row label="Source">{p.sourceText}</Row>
              <Row label="Channel">{p.channel}</Row>
              <Row label="Tools version">{p.toolsVersion}</Row>
              <Row label="Person">{p.person ? <PersonValue person={p.person} /> : null}</Row>
              <Row label="Linked to">{isUnlinked(p) ? <UnlinkedNote /> : p.linkText}</Row>
              <Row label="Written"><When value={p.ts} /></Row>
              <Row label="Received"><When value={p.receivedAt} /></Row>
              <Row label="Findings">{p.findingsText}</Row>
              <Row label="Size">{p.overSoftLimit ? <Pill tone="warn" title={POSTMORTEM_SOFT_LIMIT_NOTE}>Over the soft limit</Pill> : ''}</Row>
              <Row label="Record id">{p.recordId ? <span className="sw-mono">{p.recordId}</span> : ''}</Row>
              <Row label="Marks"><MarkPills marks={p.marks} /></Row>
            </dl>
          </FleetSection>

          <FleetSection id="ait-findings" title="Findings" lede="What the postmortem found, in its order. Open one to read it as received.">
            <FleetGrid title="Findings" tableRef={tableRef} compact flush rowHeight={ROW_HEIGHT_TWO_LINE} tableSettings={false}>
              <TableAgClient ref={tableRef} queryKey="postmortem-findings" apiList={gridList} getRowId={findingID} rowHeight={ROW_HEIGHT_TWO_LINE}
                onRowClicked={row => row && setOpenID(row.id)}
                emptyState={{ Icon: IconAiDocument, title: 'No findings', message: 'This postmortem found nothing.' }}>
                <ColumnFormat field="ordinal" header="Order" sortable={false} fit="content" render={orderCell} />
                <ColumnFormat field="titleText" header="Finding" sortable={false} flex={2} stacked render={titleCell} />
                <ColumnFormat field="severityText" header="Severity" sortable={false} fit="content" render={severityCell} />
                <ColumnFormat field="categoryText" header="Category" sortable={false} fit="content" render={categoryCell} />
                <ColumnFormat field="toolText" header="Tool" sortable={false} render={toolCell} />
                <ColumnFormat field="marksText" header="Marks" sortable={false} flex={1} render={marksCell} />
                <ColumnMenu menu={menu} />
              </TableAgClient>
            </FleetGrid>
          </FleetSection>

          <FleetSection id="ait-body" title="Postmortem as received" lede="The record as it was sent, without its owner and stamp (the server drops them).">
            <pre className="ak-body sw-mono">{jsonText(p.body)}</pre>
          </FleetSection>
        </>}
      {openRow && <FindingDrawer row={openRow} onClose={() => setOpenID(null)} />}
      {marking && <MarkDialog target={marking} consumers={consumers} onCancel={() => setMarking(null)} onMarked={() => marked(marking)} />}
      {writes.dialog}
      {findingWrites.dialog}
    </FleetPage>
  )
}

// A postmortem's page: another postmortem (the address changed under the
// mounted page) is a fresh page, so nothing of the last one's findings, open
// finding or dialog carries over.
export const PostmortemDetail = () => {
  const { postmortemID } = useParams()
  return <PostmortemPage key={postmortemID} postmortemID={postmortemID} />
}
